import React, { useCallback, useLayoutEffect, useRef } from 'react';

import { HistoryOfChangesItem } from 'pages/item/v2/types/history-of-changes';
import getScoreSeverity from 'utils/get-score-severity';
import isEmpty from 'utils/is-empty';
import toMeaningfulFixed from 'utils/to-meaningful-fixed';

import ScorePane, { ScorePaneRef } from '../ScorePane';
import TrendTooltip from '../TrendTooltip';

interface Props {
    epss: {
        epss: number;
        percentile: number;
    }[];
}

const Epss: React.FC<Props> = ({ epss }) => {
    const scorePaneRef = useRef<ScorePaneRef>(null);
    useLayoutEffect(() => scorePaneRef.current?.setIsOpen(window.innerWidth >= 960), []);

    const getChart = useCallback((data: HistoryOfChangesItem[]) => {
        if (data.every(({ changes }) => 'epss' in changes === false)) return null;
        return data.reduce((output, { version, published, changes }) => {
            const score = changes.epss
                ? Math.max(...changes.epss.map(({ epss }) => epss), 0)
                : output.at(-1)?.score ?? 0;
            output.push({ version, score, published });
            return output;
        }, [] as { score: number; version: number; published: Date }[]);
    }, []);

    if (!Array.isArray(epss) || isEmpty(epss)) {
        return null;
    }

    const score = epss.reduce((max, { epss, percentile }) => (epss > max.epss ? { epss, percentile } : max), {
        epss: 0,
        percentile: 0
    });

    return (
        <TrendTooltip getChart={getChart} title="EPSS score statistics" scale={0.1}>
            <ScorePane
                ref={scorePaneRef}
                name="EPSS"
                score={toMeaningfulFixed(score.epss, 3)}
                severity={getScoreSeverity(score.epss, 0.1)}
                vectorItems={{ percentile: (score.percentile * 100).toFixed(1) + '%' }}
            />
        </TrendTooltip>
    );
};

export default Epss;
